import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SalaryRangeChart = ({ data, description }) => {
  // Data preparation for chart - use salary data from candidates by role
  const salaryRangeByRoleData = () => {
    const roleData = {};
    
    data.forEach(candidate => {
      if (!candidate.salary || !candidate.salary.amount || candidate.salary.amount === 0) return;
      if (!candidate.role || candidate.role.trim() === '') return;
      
      const role = candidate.role;
      const key = `${role} (${candidate.seniority || 'All Levels'})`;
      if (!roleData[key]) {
        roleData[key] = {
          min: Infinity,
          max: -Infinity,
          sum: 0,
          count: 0
        };
      }
      
      roleData[key].min = Math.min(roleData[key].min, candidate.salary.amount);
      roleData[key].max = Math.max(roleData[key].max, candidate.salary.amount);
      roleData[key].sum += candidate.salary.amount;
      roleData[key].count++;
    });
    
    return Object.entries(roleData)
      .map(([role, data]) => ({
        name: role,
        minimum: data.min === Infinity ? 0 : data.min,
        maximum: data.max === -Infinity ? 0 : data.max,
        average: Math.round(data.sum / data.count)
      }))
      .filter(item => item.minimum > 0) // Filter out entries with no valid salary data
      .sort((a, b) => b.average - a.average);
  };

  const chartData = salaryRangeByRoleData().slice(0, 6);
  const totalRoles = salaryRangeByRoleData().length;

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '900ms'}}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Salary Range by Role</h3>
          <p className="text-sm text-gray-500">Compensation analysis by role and seniority</p>
        </div>
        <div className="text-right">
          <div className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-full mb-1">
            Compensation
          </div>
          <p className="text-xs text-gray-500">{totalRoles} role combinations</p>
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      <div className="bg-gray-50 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart 
            data={chartData} 
            layout="vertical" 
            margin={{ top: 20, right: 30, left: 140, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} opacity={0.6} />
            <XAxis 
              type="number" 
              tickLine={false} 
              axisLine={false} 
              style={{ fontSize: '11px', fill: '#6b7280' }} 
              tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
              label={{ value: 'Salary (€)', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fontSize: '11px', fill: '#6b7280' } }}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={140} 
              tickLine={false} 
              axisLine={false} 
              style={{ fontSize: '10px', fill: '#6b7280' }} 
              tick={{ width: 140, textAnchor: 'end' }}
              tickFormatter={(value) => value.length > 25 ? value.substring(0, 25) + '...' : value}
            />
            <Tooltip 
              contentStyle={{ 
                borderRadius: '12px', 
                border: 'none', 
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                fontSize: '12px',
                backgroundColor: 'white',
                padding: '12px'
              }}
              labelStyle={{ fontWeight: '600', color: '#374151', marginBottom: '8px' }}
              formatter={(value, name) => {
                const labels = {
                  'minimum': 'Minimum Salary',
                  'average': 'Average Salary', 
                  'maximum': 'Maximum Salary'
                };
                return [`€${value.toLocaleString()}`, labels[name] || name];
              }}
              labelFormatter={(label) => label.length > 30 ? label.substring(0, 30) + '...' : label}
            />
            <Legend 
              iconType="rect" 
              iconSize={12} 
              align="center" 
              verticalAlign="top" 
              wrapperStyle={{ paddingBottom: '20px', fontSize: '11px' }} 
            />
            <Bar dataKey="minimum" fill="#3b82f6" name="Minimum" barSize={12} radius={[0, 2, 2, 0]} />
            <Bar dataKey="average" fill="#f59e0b" name="Average" barSize={12} radius={[0, 2, 2, 0]} />
            <Bar dataKey="maximum" fill="#ef4444" name="Maximum" barSize={12} radius={[0, 2, 2, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SalaryRangeChart;
